import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Modal,
  notification,
  Skeleton,
  Spin,
  Tabs,
} from '@pankod/refine-antd'
import { ZoomIn } from 'lucide-react'

import {
  TCertificatePreviewParticipant,
  TCertificateTTEGetSubmissionResponse,
} from '../types'
import { ParticipantPreviewSelect } from './ParticipantPreviewSelect'
import { PdfCanvasPreview } from './PdfCanvasPreview'
import { getSVGStringFromUrl } from '@resources/lms-management/certificate-template/fabric/utils/canvas'

type TModalPreview =
  | {
      title: string
      type: 'svg'
      content: string
    }
  | {
      title: string
      type: 'pdf'
      url: string
      page: number
    }

type TCertificatePreviewProps = {
  pengajuan: TCertificateTTEGetSubmissionResponse
  submissionId: string
}

export const CertificatePreview: React.FC<TCertificatePreviewProps> = ({
  pengajuan,
  submissionId,
}) => {
  const [selectedParticipant, setSelectedParticipant] =
    useState<TCertificatePreviewParticipant>()
  const [activePage, setActivePage] = useState('front')
  const [svgString, setSvgString] = useState({ front: '', back: '' })
  const [loadingSvg, setLoadingSvg] = useState(false)
  const [svgError, setSvgError] = useState(false)
  const [modalPreview, setModalPreview] = useState<TModalPreview>()
  const [isPreviewReady, setIsPreviewReady] = useState(false)
  const [participantPageCount, setParticipantPageCount] = useState<number>()
  const failedParticipantIdRef = useRef<string>()

  const generateDefaultPreview = useCallback(async () => {
    if (!pengajuan.frontTemplateUrl && !pengajuan.backTemplateUrl) return

    setLoadingSvg(true)
    setSvgError(false)
    setSvgString({ front: '', back: '' })

    try {
      const [front, back] = await Promise.all([
        pengajuan.frontTemplateUrl
          ? getSVGStringFromUrl(pengajuan.frontTemplateUrl, undefined)
          : Promise.resolve(''),
        pengajuan.backTemplateUrl
          ? getSVGStringFromUrl(pengajuan.backTemplateUrl, undefined)
          : Promise.resolve(''),
      ])
      setSvgString({ front, back })
    } catch (error) {
      console.error('Error generating certificate preview:', error)
      setSvgError(true)
    } finally {
      setLoadingSvg(false)
    }
  }, [pengajuan.backTemplateUrl, pengajuan.frontTemplateUrl])

  useEffect(() => {
    void generateDefaultPreview()
  }, [generateDefaultPreview])

  useEffect(() => {
    setSelectedParticipant(undefined)
    setActivePage('front')
    setModalPreview(undefined)
    setParticipantPageCount(undefined)
    failedParticipantIdRef.current = undefined
  }, [submissionId])

  useEffect(() => {
    setActivePage('front')
  }, [selectedParticipant?.certificateDocumentUrl, selectedParticipant?.id])

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (modalPreview) {
      setIsPreviewReady(false)
      timer = setTimeout(() => {
        setIsPreviewReady(true)
      }, 350)
    }

    return () => clearTimeout(timer)
  }, [modalPreview])

  const fallbackToDefault = useCallback(
    (participant: TCertificatePreviewParticipant) => {
      const participantId = String(participant.id)
      if (failedParticipantIdRef.current === participantId) return

      failedParticipantIdRef.current = participantId
      setSelectedParticipant(undefined)
      setActivePage('front')
      setModalPreview(undefined)
      setParticipantPageCount(undefined)
      notification.warning({
        message: 'Preview peserta gagal dimuat',
        description: `Preview ${participant.name} tidak dapat dimuat. Template default ditampilkan kembali.`,
      })
    },
    [],
  )

  const handleParticipantSelect = (
    participant?: TCertificatePreviewParticipant,
  ) => {
    failedParticipantIdRef.current = undefined
    setParticipantPageCount(undefined)

    if (participant && !participant.certificateDocumentUrl) {
      fallbackToDefault(participant)
      return
    }

    setSelectedParticipant(participant)
  }

  const handleParticipantPdfLoad = useCallback((pageCount: number) => {
    setParticipantPageCount(Math.min(pageCount, 2))
  }, [])

  const renderSvg = (title: string, svgContent: string) => {
    if (loadingSvg) {
      return (
        <div className="w-full flex items-center justify-center p-4 border rounded bg-gray-50">
          <Skeleton.Image active className="!w-[200px] !h-[282px]" />
        </div>
      )
    }

    if (!svgContent) {
      return (
        <div className="w-full p-4 border rounded bg-gray-50 text-center text-gray-400">
          Tidak ada template
        </div>
      )
    }

    return (
      <button
        type="button"
        className="relative block w-full max-w-[500px] border border-solid border-gray-300 rounded shadow-sm bg-white overflow-hidden cursor-pointer group mb-4 p-0"
        onClick={() =>
          setModalPreview({
            title,
            type: 'svg',
            content: svgContent,
          })
        }
      >
        <div
          className="[&>svg]:!w-full [&>svg]:!h-auto flex items-center justify-center bg-white transition-all duration-300 group-hover:scale-[1.02]"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
        <PreviewOverlay />
      </button>
    )
  }

  const renderPdf = (
    title: string,
    url: string,
    page: number,
    participant: TCertificatePreviewParticipant,
    onLoad?: (pageCount: number) => void,
  ) => (
    <button
      type="button"
      className="relative block w-full max-w-[500px] border border-solid border-gray-300 rounded shadow-sm bg-white overflow-hidden cursor-pointer group mb-4 p-0"
      onClick={() =>
        setModalPreview({
          title,
          type: 'pdf',
          url,
          page,
        })
      }
    >
      <PdfCanvasPreview
        url={url}
        page={page}
        onLoad={onLoad}
        className="w-full bg-white transition-all duration-300 group-hover:scale-[1.02]"
        onError={() => fallbackToDefault(participant)}
      />
      <PreviewOverlay />
    </button>
  )

  const tabItems = [
    {
      label: 'Halaman Depan',
      key: 'front',
      children: selectedParticipant
        ? renderPdf(
            'Halaman Depan',
            selectedParticipant.certificateDocumentUrl,
            1,
            selectedParticipant,
            handleParticipantPdfLoad,
          )
        : renderSvg('Halaman Depan', svgString.front),
    },
  ]

  const hasBackPage = selectedParticipant
    ? participantPageCount === 2
    : !!pengajuan.backTemplateUrl

  if (hasBackPage) {
    tabItems.push({
      label: 'Halaman Belakang',
      key: 'back',
      children: selectedParticipant
        ? renderPdf(
            'Halaman Belakang',
            selectedParticipant.certificateDocumentUrl,
            2,
            selectedParticipant,
          )
        : renderSvg('Halaman Belakang', svgString.back),
    })
  }

  return (
    <>
      <ParticipantPreviewSelect
        submissionId={submissionId}
        selectedParticipant={selectedParticipant}
        onSelect={handleParticipantSelect}
      />

      {svgError && !selectedParticipant ? (
        <Alert
          type="error"
          message="Gagal memuat template sertifikat"
          showIcon
          className="mt-4"
        />
      ) : (
        <div className="certificate-preview-container">
          <Tabs
            activeKey={activePage}
            items={tabItems}
            onChange={setActivePage}
          />
        </div>
      )}

      <Modal
        open={!!modalPreview}
        title={`Pratinjau - ${modalPreview?.title || ''}`}
        onCancel={() => setModalPreview(undefined)}
        footer={null}
        width="90vw"
        centered
        destroyOnClose
      >
        <div className="flex justify-center items-center min-h-[50vh]">
          {!isPreviewReady ? (
            <div className="w-full flex flex-col items-center justify-center h-full gap-3 text-gray-500">
              <Spin size="large" />
              <span>Memuat pratinjau...</span>
            </div>
          ) : modalPreview?.type === 'svg' ? (
            <div
              className="[&>svg]:!max-h-[80vh] [&>svg]:!w-auto w-fit bg-white border border-solid border-gray-300 rounded"
              dangerouslySetInnerHTML={{ __html: modalPreview.content }}
            />
          ) : modalPreview?.type === 'pdf' && selectedParticipant ? (
            <PdfCanvasPreview
              url={modalPreview.url}
              page={modalPreview.page}
              className="w-full max-w-[1200px] bg-white border border-solid border-gray-300 rounded"
              onError={() => fallbackToDefault(selectedParticipant)}
            />
          ) : null}
        </div>
      </Modal>
    </>
  )
}

const PreviewOverlay = () => (
  <span className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
    <span className="flex flex-col items-center text-white">
      <ZoomIn className="w-10 h-10 mb-2 opacity-90" />
      <span className="font-medium text-sm">Perbesar pratinjau</span>
    </span>
  </span>
)
